import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { PhoneInput } from '../ui/phone-input'
import { Label } from '../ui/label'
import { Switch } from '../ui/switch'
import { CountrySelect } from '../ui/country-select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { SenderProfile, SenderProfileCreate, SenderProfileUpdate } from '../../types/settings'

interface SenderProfileFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: SenderProfileCreate | SenderProfileUpdate) => Promise<void>
  profile?: SenderProfile | null
  isLoading?: boolean
}

export function SenderProfileForm({
  open,
  onClose,
  onSubmit,
  profile,
  isLoading = false,
}: SenderProfileFormProps) {
  const { t } = useTranslation()
  const isEditing = !!profile

  const [formData, setFormData] = useState<SenderProfileCreate>({
    name: '',
    companyName: '',
    taxId: '',
    street: '',
    city: '',
    postalCode: '',
    countryCode: '',
    email: '',
    phone: '',
    isDefault: false,
  })

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name,
        companyName: profile.companyName,
        taxId: profile.taxId || '',
        street: profile.street || '',
        city: profile.city || '',
        postalCode: profile.postalCode || '',
        countryCode: profile.countryCode || '',
        email: profile.email || '',
        phone: profile.phone || '',
        isDefault: profile.isDefault,
      })
    } else {
      setFormData({
        name: '',
        companyName: '',
        taxId: '',
        street: '',
        city: '',
        postalCode: '',
        countryCode: '',
        email: '',
        phone: '',
        isDefault: false,
      })
    }
  }, [profile, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(formData)
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing
                ? t('settings.senderProfiles.edit')
                : t('settings.senderProfiles.add')}
            </DialogTitle>
            <DialogDescription>
              {t('settings.senderProfiles.formDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="profileName">{t('settings.senderProfiles.profileName')}</Label>
              <Input
                id="profileName"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('settings.senderProfiles.profileNamePlaceholder')}
                required
                disabled={isLoading}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="companyName">
                  {t('settings.senderProfiles.companyName')}
                </Label>
                <Input
                  id="companyName"
                  value={formData.companyName}
                  onChange={(e) =>
                    setFormData({ ...formData, companyName: e.target.value })
                  }
                  placeholder={t('settings.senderProfiles.companyNamePlaceholder')}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="taxId">{t('settings.senderProfiles.taxId')}</Label>
                <Input
                  id="taxId"
                  value={formData.taxId}
                  onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                  placeholder={t('settings.senderProfiles.taxIdPlaceholder')}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="street">{t('settings.senderProfiles.street')}</Label>
              <Input
                id="street"
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                placeholder={t('settings.senderProfiles.streetPlaceholder')}
                disabled={isLoading}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="city">{t('settings.senderProfiles.city')}</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder={t('settings.senderProfiles.cityPlaceholder')}
                  disabled={isLoading}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="postalCode">
                  {t('settings.senderProfiles.postalCode')}
                </Label>
                <Input
                  id="postalCode"
                  value={formData.postalCode}
                  onChange={(e) =>
                    setFormData({ ...formData, postalCode: e.target.value })
                  }
                  placeholder={t('settings.senderProfiles.postalCodePlaceholder')}
                  disabled={isLoading}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="country">{t('settings.senderProfiles.country')}</Label>
                <CountrySelect
                  id="country"
                  value={formData.countryCode}
                  onChange={(value) => setFormData({ ...formData, countryCode: value })}
                  disabled={isLoading}
                  placeholder={t('settings.senderProfiles.selectCountry')}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">{t('settings.senderProfiles.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t('settings.senderProfiles.emailPlaceholder')}
                  disabled={isLoading}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="phone">{t('settings.senderProfiles.phone')}</Label>
                <PhoneInput
                  id="phone"
                  value={formData.phone}
                  onChange={(val) => setFormData({ ...formData, phone: val })}
                  placeholder={t('settings.senderProfiles.phonePlaceholder')}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isDefault"
                checked={formData.isDefault}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isDefault: checked })
                }
                disabled={isLoading}
              />
              <Label htmlFor="isDefault">
                {t('settings.senderProfiles.setAsDefault')}
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving')}
                </>
              ) : (
                t('common.save')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
